import {
  Badge,
  Box,
  Button,
  ComboboxItem,
  Flex,
  Grid,
  Group,
  Loader,
  LoadingOverlay,
  Select,
  SelectProps,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDebouncedState, useDisclosure, useHotkeys } from "@mantine/hooks";
import {
  IconBuildingSkyscraper,
  IconBuildingWarehouse,
  IconCalendar,
  IconCheck,
  IconFileTypeCsv,
  IconSearch,
  IconStatusChange,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useDeferredValue, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { repositoryMdm } from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import {
  changeSearchValue,
  getValueById,
} from "../../../_base/helper/FunctionHelper";
import { formatDateTransfer } from "../../../common/FormatDate/FormatDate";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";
import {
  getBranchSelect,
  getInventorySelectPos,
} from "../../../service/getSelectApi";
import { BASE_API_MDM } from "../../../config";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";

type ColumnPinningType = {
  left?: string[];
  right?: string[];
};

// Màu badge theo trạng thái phiếu mượn
const statusColor: Record<string, string> = {
  YEU_CAU_XUAT_MUON: "blue",
  DA_XUAT: "orange",
  DA_NHAP_VE: "green",
  DA_THU: "teal",
  DA_CHI: "yellow",
};

// Màu badge theo loại phiếu
const ticketTypeColor: Record<string, string> = {
  CHO_MUON: "orange",
  NHAP_LAI: "blue",
};

// Danh sách loại phiếu dùng cho bộ lọc
const optionTicketType = [
  { label: "Bảo hành", value: "true" },
  { label: "Cho mượn", value: "false" },
];

// Hiển thị ô tiền tệ
const renderCurrency = ({ renderedCellValue }: any) =>
  renderedCellValue || Number(renderedCellValue) === 0 ? (
    <Text fw={500} size="12.5px">
      {Number(renderedCellValue)?.toLocaleString()} VNĐ
    </Text>
  ) : (
    <></>
  );

// Hiển thị ô ngày tháng
const renderDate = ({ renderedCellValue }: any) => (
  <Text size="12.5px" fw={"500"}>
    {renderedCellValue && formatDateTransfer(renderedCellValue)}
  </Text>
);

const ListOfBorrowedItems = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [visible, { toggle, open, close }] = useDisclosure(false);

  const navigate = useNavigate();
  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";

  // Mặc định: từ ngày 1 đến ngày cuối cùng của tháng hiện tại
  const now = new Date();
  const toIsoVN = (d: Date) =>
    new Date(d.getTime() + 7 * 3600 * 1000).toISOString();
  const defaultStartDate = toIsoVN(
    new Date(now.getFullYear(), now.getMonth(), 1),
  );
  const defaultEndDate = toIsoVN(
    new Date(now.getFullYear(), now.getMonth() + 1, 0),
  );

  const [search, setSearch] = useState<any>({
    StartDate:
      _searchJson && checkedPinSearch
        ? _searchJson?.StartDate
        : defaultStartDate,
    EndDate:
      _searchJson && checkedPinSearch ? _searchJson?.EndDate : defaultEndDate,
    Status: _searchJson && checkedPinSearch ? _searchJson?.Status : "",
    Type: _searchJson && checkedPinSearch ? _searchJson?.TicketType : "",
    BranchId: _searchJson && checkedPinSearch ? _searchJson?.BranchId : "",
    InventoryId:
      _searchJson && checkedPinSearch ? _searchJson?.InventoryId : "",
  });
  const [keySearch, setKeySearch] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.keySearch : "",
    300,
  );

  const [dataBranchSelect, setDataBranchSelect] = useState<ComboboxItem[]>([]);
  const [dataInventorySelect, setDataInventorySelect] = useState<
    ComboboxItem[]
  >([]);
  const dataStatusOption = [
    { value: "YEU_CAU_XUAT_MUON", label: "Yêu cầu xuất mượn" },
    { value: "DA_THU", label: "Đã thu tiền" },
    { value: "DA_XUAT", label: "Đã xuất kho" },
    { value: "DA_NHAP_VE", label: "Đã nhập về" },
    { value: "DA_CHI", label: "Đã chi" },
  ];

  // Trang chi tiết nhận id qua location.state khi navigate, nhưng state không sống sót
  // khi mở tab mới -> phải đẩy id vào query string để tab mới đọc được.
  const buildBorrowDetailUrl = (row: any): string | undefined => {
    const id = row?.original?.id;
    if (id === null || id === undefined || id === "") return undefined;

    const params = new URLSearchParams();
    params.set("id", String(id));
    if (row.original.status) params.set("status", String(row.original.status));

    return `/warranty/detail-borrowed-goods?${params.toString()}`;
  };

  const buildReceiveDetailUrl = (row: any): string | undefined => {
    const id = row?.original?.receiveId;
    if (id === null || id === undefined || id === "") return undefined;

    return `/warranty/detail-receive-ticket?id=${Number(id)}`;
  };

  // Ô mã phiếu render bằng thẻ <a> thật để Ctrl/Cmd + click hoặc click chuột giữa
  // mở được tab nền theo cơ chế native của trình duyệt.
  // Click thường không điều hướng (tránh rời trang ngoài ý muốn), double-click mở như cũ.
  const renderDetailLinkCell = (
    renderedCellValue: React.ReactNode,
    url: string | undefined,
    onNavigate: () => void,
  ) => {
    const badge = (
      <Badge
        variant="dot"
        color={renderedCellValue ? "green" : "red"}
        style={{ cursor: url ? "pointer" : "default" }}
      >
        {renderedCellValue ? renderedCellValue : "Không xác định"}
      </Badge>
    );

    if (!url) return badge;

    return (
      <Tooltip label="Double click để xem chi tiết, Ctrl + click để mở tab mới">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            // Có phím bổ trợ -> nhường trình duyệt xử lý, tuyệt đối không preventDefault
            // vì sẽ chặn luôn cả việc mở tab nền.
            if (e.ctrlKey || e.metaKey || e.shiftKey) return;
            e.preventDefault();
          }}
          onDoubleClick={onNavigate}
          style={{
            color: "inherit",
            textDecoration: "none",
            display: "block",
          }}
        >
          {badge}
        </a>
      </Tooltip>
    );
  };

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "borrowCode",
        header: "Mã phiếu mượn",
        size: 180,
        Cell: ({ renderedCellValue, row }) =>
          renderDetailLinkCell(
            renderedCellValue,
            buildBorrowDetailUrl(row),
            () => detailPYCXM(row.original.id!, row.original.status),
          ),
      },
      {
        accessorKey: "type",
        header: "Loại",
        Cell: ({ row }) => {
          const type = row.original.type;
          if (type === null || type === undefined) return <></>;
          return (
            <Badge color={type === true ? "blue" : "green"}>
              {type === true ? "Bảo hành" : "Cho mượn"}
            </Badge>
          );
        },
      },
      {
        accessorKey: "statusName",
        header: "Trạng thái",
        Cell: ({ renderedCellValue, row }) =>
          renderedCellValue || row.original.status ? (
            <Badge color={statusColor[row.original.status] ?? "gray"}>
              {renderedCellValue ||
                getValueById(
                  String(row.original.status),
                  dataStatusOption,
                  "label",
                ) ||
                row.original.status}
            </Badge>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "receiveTicketCode",
        header: "Mã phiếu tiếp nhận",
        Cell: ({ renderedCellValue, row }) =>
          renderDetailLinkCell(
            renderedCellValue,
            buildReceiveDetailUrl(row),
            () => detailPNHL(row.original.receiveId),
          ),
        size: 180,
      },
      { accessorKey: "serial", header: "Serial", size: 130 },
      { accessorKey: "itemCode", header: "Mã sản phẩm", size: 140 },
      { accessorKey: "itemName", header: "Tên sản phẩm", size: 320 },
      {
        accessorKey: "quantity",
        header: "Số lượng",
        Cell: ({ renderedCellValue }) => (
          <Text fw={500} size="12.5px">
            {renderedCellValue}
          </Text>
        ),
      },
      { accessorKey: "detailStatus", header: "Tình trạng SP", size: 160 },
      {
        accessorKey: "acessory",
        header: "Phụ kiện",
        Cell: ({ renderedCellValue }) => (
          <Text fw={500} size="12.5px">
            {renderedCellValue ?? ""}
          </Text>
        ),
      },
      {
        accessorKey: "warrantyTerm",
        header: "Thời hạn BH (tháng)",
        size: 175,
      },
      { accessorKey: "sellingPrice", header: "Giá bán", Cell: renderCurrency },
      {
        accessorKey: "actualPrice",
        header: "Giá thực tế",
        Cell: renderCurrency,
      },
      { accessorKey: "deposit", header: "Đặt cọc", Cell: renderCurrency },
      {
        accessorKey: "totalDeposit",
        header: "Tổng đặt cọc",
        Cell: renderCurrency,
      },
      { accessorKey: "cusName", header: "Khách hàng", size: 160 },
      { accessorKey: "cusPhoneNumber", header: "SĐT khách hàng", size: 150 },
      { accessorKey: "cusAddress", header: "Địa chỉ", size: 300 },
      {
        accessorKey: "inventoryName",
        header: "Kho",
        size: 240,
        Cell: ({ renderedCellValue, row }) => (
          <Text size="12.5px" fw={"500"}>
            {row.original.inventoryCode
              ? `(${row.original.inventoryCode}) `
              : ""}
            {renderedCellValue}
          </Text>
        ),
      },
      { accessorKey: "branchName", header: "Chi nhánh", size: 200 },
      { accessorKey: "departmentName", header: "Phòng ban", size: 220 },
      {
        accessorKey: "imTransactionCode",
        header: "Mã phiếu nhập",
        size: 160,
        Cell: ({ renderedCellValue }) => (
          <Badge variant="dot" color={renderedCellValue ? "green" : "red"}>
            {renderedCellValue ? renderedCellValue : "Không xác định"}
          </Badge>
        ),
      },
      {
        accessorKey: "exTransactionCode",
        header: "Mã phiếu xuất",
        size: 160,
        Cell: ({ renderedCellValue }) => (
          <Badge variant="dot" color={renderedCellValue ? "green" : "red"}>
            {renderedCellValue ? renderedCellValue : "Không xác định"}
          </Badge>
        ),
      },
      { accessorKey: "createByName", header: "Người tạo", size: 180 },
      {
        accessorKey: "createDate",
        header: "Ngày tạo",
        Cell: renderDate,
      },
      {
        accessorKey: "buyingDate",
        header: "Ngày mua",
        Cell: renderDate,
      },
      {
        accessorKey: "detailNote",
        header: "Ghi chú",
      },
    ],
    [dataStatusOption],
  );

  const detailPYCXM = (id: string | number, status: string) => {
    navigate("/warranty/detail-borrowed-goods", {
      state: { id, status },
    });
  };

  const detailPNHL = (id: string | number) => {
    navigate("/warranty/detail-receive-ticket", {
      state: { id: Number(id) },
    });
  };

  // const detailOutWard = (id: string | number) => {
  //   navigate("/warehouse-config/detail-warehouse-out-ward", {
  //     state: {
  //       transactionCode: id,
  //       type: "XUAT_MUON_BAO_HANH",
  //       isLocked: true,
  //     },
  //   });
  // };

  const renderSelectOptionStatus: SelectProps["renderOption"] = ({
    option,
    checked,
  }) => (
    <Group flex="1" gap="xs">
      <Badge color={statusColor[option.value] ?? "gray"} variant="filled">
        {option.label}
      </Badge>
      {checked && (
        <IconCheck size={"14px"} style={{ marginInlineStart: "auto" }} />
      )}
    </Group>
  );

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `/api/v1/TblWarrantyExportItemBorrowTicket/bang-ke-hang-muon?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      url += `&${params?.toString()}`;

      const res = await repositoryMdm.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify({
          ...search,
          keySearch: keySearch,
        }),
      );
    }
  };

  const exportDataExcel = async () => {
    open();
    try {
      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (keySearch.length > 0) {
        params.append("KeySearch", keySearch.trim());
      }

      const queryString = params.toString();
      const baseUrl = `/api/v1/TblWarrantyExportItemBorrowTicket/export-bang-ke-hang-muon${
        queryString ? `?${queryString}` : ""
      }`;

      const _url = BASE_API_MDM + baseUrl;
      const token = localStorage.getItem("token");

      const response = await fetch(_url, {
        method: "GET",
        headers: {
          Accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
        },
      });

      if (!response.ok) {
        NotificationExtension.Fails("Export Excel thất bại !");
        return;
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        NotificationExtension.Fails("Không có dữ liệu để export !");
        return;
      }

      const objectUrl = window.URL.createObjectURL(blob);
      const fileName = `bang_ke_hang_muon_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      NotificationExtension.Success("Export Excel thành công !");
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Export Excel thất bại !");
    } finally {
      close();
    }
  };

  const getDataBranchSelect = async () => {
    const res = await getBranchSelect();

    if (res) {
      setDataBranchSelect(
        res
          ?.filter(
            (item: any) =>
              item.value != null && item.text != null && item.value !== "-1",
          )
          ?.map((item: any) => ({
            value: String(item.value),
            label: item.text,
          })),
      );
    }
  };

  const getDataInventorySelect = async () => {
    const res = await getInventorySelectPos();

    if (res) {
      setDataInventorySelect(
        res
          ?.filter(
            (item: any) =>
              item.id != null && item.code != null && item.name != null,
          )
          ?.map((item: any) => ({
            value: String(item.id),
            label: `(${item.code}) ${item.name}`,
          })),
      );
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination?.pageIndex, pagination?.pageSize]);

  useEffect(() => {
    Promise.all([getDataBranchSelect(), getDataInventorySelect()]);
  }, []);

  const getColumnPinningStorage = () => {
    const columnPinningStorage = localStorage.getItem(
      "columnPinningListOfBorrowedItems",
    );
    return columnPinningStorage
      ? JSON.parse(columnPinningStorage)
      : {
          left: ["mrt-row-numbers", "borrowCode"],
          right: [],
        }; // Giá trị mặc định
  };

  // Lưu columnPinning vào state
  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>(
    getColumnPinningStorage(),
  );
  // Hàm lưu vào localStorage
  useEffect(() => {
    localStorage.setItem(
      "columnPinningListOfBorrowedItems",
      JSON.stringify(columnPinning),
    );
  }, [columnPinning]);

  useEffect(() => {
    //   startTransition(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
    //  });
  }, [rowSelectionDeferred]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (150 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowNumbers: true,
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: false,
    getRowId: (row) => `${row.id}-${row.detailId}`,
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        // left: ["mrt-row-numbers", "maVt"],
        // right: ["action"],
        ...columnPinning,
      },
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    onColumnPinningChange: (newState) => {
      setColumnPinning(newState);
    },
    onRowSelectionChange: setRowSelection,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "500", //conditional styling
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
      columnPinning,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  useHotkeys([
    [
      "Enter",
      () => {
        fetchData();
      },
    ],
  ]);

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                {/* <Title size={15}> Đang upload tài liệu...</Title> */}
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <_breadcrumb></_breadcrumb>
          <Button
            leftSection={<IconFileTypeCsv color="#15aabf" size={14} />}
            onClick={() => exportDataExcel()}
          >
            Export Excel
          </Button>
        </Flex>
        <Grid mt={10} w={"100%"}>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập từ khóa tìm kiếm"
              defaultValue={keySearch}
              onChange={(e) => setKeySearch(e.currentTarget.value)}
              leftSection={<IconSearch color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày bắt đầu"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              value={
                search.StartDate && search.StartDate?.length > 0
                  ? new Date(search.StartDate ?? "")
                  : null
              }
              maxDate={
                search.EndDate && search.EndDate?.length > 0
                  ? new Date(search.EndDate)
                  : new Date()
              }
              onChange={(e) => {
                changeSearchValue(
                  e
                    ? new Date(
                        new Date(e).getTime() + 7 * 3600 * 1000,
                      )?.toISOString()
                    : "",
                  "StartDate",
                  setSearch,
                );
              }}
              leftSection={<IconCalendar color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày kết thúc"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              value={
                search.EndDate && search.EndDate?.length > 0
                  ? new Date(search.EndDate ?? "")
                  : null
              }
              minDate={
                search.StartDate && search.StartDate?.length > 0
                  ? new Date(search.StartDate)
                  : undefined
              }
              onChange={(e) => {
                changeSearchValue(
                  e
                    ? new Date(
                        new Date(e).getTime() + 7 * 3600 * 1000,
                      )?.toISOString()
                    : "",
                  "EndDate",
                  setSearch,
                );
              }}
              leftSection={<IconCalendar color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Trạng thái"
              leftSection={<IconStatusChange color="#15aabf" />}
              data={dataStatusOption}
              value={search?.Status || null}
              onChange={(e) =>
                changeSearchValue(e ? e : "", "Status", setSearch)
              }
              clearable
              renderOption={renderSelectOptionStatus}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Loại"
              leftSection={<IconStatusChange color="#15aabf" />}
              data={optionTicketType}
              value={search?.Type || null}
              onChange={(e) => changeSearchValue(e ? e : "", "Type", setSearch)}
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Chi nhánh"
              data={dataBranchSelect}
              value={search?.BranchId || null}
              leftSection={<IconBuildingSkyscraper color="#15aabf" />}
              onChange={(e) =>
                changeSearchValue(e ? e : "", "BranchId", setSearch)
              }
              searchable
              clearable
              nothingFoundMessage="Không tìm thấy dữ liệu !"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Kho"
              data={dataInventorySelect}
              value={search?.InventoryId || null}
              leftSection={<IconBuildingWarehouse color="#15aabf" />}
              onChange={(e) =>
                changeSearchValue(e ? e : "", "InventoryId", setSearch)
              }
              searchable
              clearable
              nothingFoundMessage="Không tìm thấy dữ liệu !"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Button
              onClick={() => fetchData()}
              leftSection={<IconSearch size={14} />}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default ListOfBorrowedItems;
